import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./CourseListView.css";
import {
  CARD_GREY,
  DARK_CARD,
  TEXT_DARK,
  EAIM_ORANGE,
  EAIM_GREEN,
  EAIM_PINK,
  EAIM_BLUE,
} from "./utils/constants";
import { pill } from "./utils/ui";

const PAGE_SIZE = 8; 
const ROTATE_MS = 30000; 

export default function CourseListView({
  startingSoon = [],
  endingSoon = [],
  showHeaders = true,
  className = "",
  animateOnMount = true,
}) {
  const initialTheme =
    document.documentElement.getAttribute("data-theme") ||
    localStorage.getItem("eaimTheme") ||
    "dark";
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    const onTheme = (e) => setTheme(e.detail);
    window.addEventListener("eaim-theme", onTheme);
    return () => window.removeEventListener("eaim-theme", onTheme);
  }, []);

  const cardBg = theme === "dark" ? DARK_CARD : CARD_GREY;
  const textColor = theme === "dark" ? "#EDEDED" : TEXT_DARK;
  const subText = theme === "dark" ? "#AAB4C8" : "#6B7785";
  const divider =
    theme === "dark" ? "rgba(255,255,255,.08)" : "rgba(17,24,39,.08)";

  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("eaimViewMode") || "grid"
  );
  useEffect(() => {
    const onViewMode = (e) => setViewMode(e.detail);
    window.addEventListener("eaim-view-mode", onViewMode);
    return () => window.removeEventListener("eaim-view-mode", onViewMode);
  }, []);
  const isList = viewMode === "list";

  const [startPage, setStartPage] = useState(0);
  const [endPage, setEndPage] = useState(0);

  const startTotalPages =
    startingSoon.length === 0
      ? 0
      : Math.ceil(startingSoon.length / PAGE_SIZE);
  const endTotalPages =
    endingSoon.length === 0 ? 0 : Math.ceil(endingSoon.length / PAGE_SIZE);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(startingSoon.length / PAGE_SIZE) - 1
    );
    if (startPage > maxPage) setStartPage(0);
  }, [startingSoon.length, startPage]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(endingSoon.length / PAGE_SIZE) - 1
    );
    if (endPage > maxPage) setEndPage(0);
  }, [endingSoon.length, endPage]);

  useEffect(() => {
    if (startingSoon.length <= PAGE_SIZE) return;
    const maxPage = Math.ceil(startingSoon.length / PAGE_SIZE) - 1;

    const id = setInterval(() => {
      setStartPage((prev) =>
        maxPage === 0 ? 0 : (prev + 1) % (maxPage + 1)
      );
    }, ROTATE_MS);

    return () => clearInterval(id);
  }, [startingSoon.length]);

  useEffect(() => {
    if (endingSoon.length <= PAGE_SIZE) return;
    const maxPage = Math.ceil(endingSoon.length / PAGE_SIZE) - 1;

    const id = setInterval(() => {
      setEndPage((prev) =>
        maxPage === 0 ? 0 : (prev + 1) % (maxPage + 1)
      );
    }, ROTATE_MS);

    return () => clearInterval(id);
  }, [endingSoon.length]);

  const startItems = useMemo(() => {
    if (!startingSoon || startingSoon.length === 0) return [];
    const from = startPage * PAGE_SIZE;
    return startingSoon.slice(from, from + PAGE_SIZE);
  }, [startingSoon, startPage]);

  const endItems = useMemo(() => {
    if (!endingSoon || endingSoon.length === 0) return [];
    const from = endPage * PAGE_SIZE;
    return endingSoon.slice(from, from + PAGE_SIZE);
  }, [endingSoon, endPage]);

  const headerBg = "#B0128A";
  const rowBg = theme === "dark" ? "rgba(255,255,255,.06)" : "#F3F4F6";
  const rowAltBg = theme === "dark" ? "rgba(255,255,255,.10)" : "#EDEEF1";

  const hasAnimatedRef = useRef(false);
  const parentVariants = useMemo(
    () => ({
      hidden: {},
      visible: { transition: { staggerChildren: 0.15 } },
    }),
    []
  );
  const rowVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    }),
    []
  );

  function formatTimeLeft(statusText) {
    if (!statusText) return "—";
    const hrMatch = statusText.match(/(\d+)\s*hr/i);
    const minMatch = statusText.match(/(\d+)\s*min/i);
    if (hrMatch || minMatch) {
      const hrs = hrMatch ? parseInt(hrMatch[1], 10) : 0;
      const mins = minMatch ? parseInt(minMatch[1], 10) : 0;
      const total = hrs * 60 + mins;
      if (total >= 60) {
        const h = Math.floor(total / 60);
        const m = total % 60;
        return m
          ? `${h} hr${h > 1 ? "s" : ""} ${m} min`
          : `${h} hr${h > 1 ? "s" : ""}`;
      }
      return `${total} min`;
    }
    if (/now/i.test(statusText)) return "now";
    return statusText;
  }

  const ROOM_COLOR_RULES = [
    { prefix: "A", min: 8, max: 13, color: EAIM_ORANGE },
    { prefix: "A", min: 4, max: 7, color: EAIM_GREEN },
    { prefix: "A", min: 1, max: 3, color: EAIM_PINK },
    { prefix: "B", min: 3, max: 11, color: EAIM_GREEN },
    { prefix: "B", min: 2, max: 2, color: EAIM_BLUE },
    { prefix: "C", min: 1, max: 6, color: EAIM_BLUE },
    { prefix: "C", min: 7, max: 8, color: EAIM_PINK },
  ];

  const DEFAULT_PREFIX_COLORS = {
    A: EAIM_GREEN,
    B: EAIM_ORANGE,
    C: EAIM_BLUE,
  };
  const DEFAULT_PILL = theme === "dark" ? "#6B7280" : "#94A3B8";

  function parseRoom(roomRaw) {
    if (!roomRaw) return { prefix: "", number: NaN };
    const s = String(roomRaw).trim().toUpperCase();
    const m = s.match(/^([A-Z]+)\s*0*([0-9]+)$/);
    if (!m) return { prefix: "", number: NaN };
    return { prefix: m[1], number: Number(m[2]) };
  }

  function pickRoomColor(roomRaw) {
    const parsed = parseRoom(roomRaw);
    const prefix = parsed.prefix;
    const number = parsed.number;

    if (prefix && Number.isFinite(number)) {
      for (const rule of ROOM_COLOR_RULES) {
        if (
          prefix === rule.prefix &&
          number >= rule.min &&
          number <= rule.max
        ) {
          return rule.color;
        }
      }
    }
    if (prefix && DEFAULT_PREFIX_COLORS[prefix]) {
      return DEFAULT_PREFIX_COLORS[prefix];
    }
    return DEFAULT_PILL;
  }

  const EmptyState = ({ accent }) => (
    <div
      className="clv-empty"
      style={{ borderColor: divider, color: subText, fontSize: "16px" }}
    >
      <div className="dot" style={{ background: accent }} />
      No classes here for now.
    </div>
  );

  const TableSection = ({
    title,
    cols,
    items,
    totalCount,
    page,
    totalPages,
  }) => {
    const shouldAnimate = animateOnMount && !hasAnimatedRef.current;

    const RowsAnimated = (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={parentVariants}
        onAnimationComplete={() => {
          hasAnimatedRef.current = true;
        }}
      >
        {items.map((it, idx) => {
          const titleText = it.title || "(No title)";
          const roomText = it.room ? `ROOM ${it.room}` : "—";
          const timeText = it.start || it.end || "—";
          const leftText = formatTimeLeft(it.statusText);
          const rowKey = `${titleText}-${it.room ?? "NA"}-${idx}`;
          const accent = pickRoomColor(it.room);

          return (
            <motion.div
              key={rowKey}
              className="clv-tr"
              style={{ background: idx % 2 ? rowAltBg : rowBg }}
              variants={rowVariants}
              transition={{ duration: 0.6 }}
            >
              <div
                className="clv-td clv-titlecell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {titleText}
              </div>

              <div
                className="clv-td clv-roomcell"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ ...pill(accent), fontSize: 11, fontWeight: 800 }}
                >
                  {roomText}
                </span>
              </div>

              <div
                className="clv-td clv-timecell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {timeText}
              </div>

              <div
                className="clv-td clv-leftcell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {leftText}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    );

    const RowsPlain = (
      <div>
        {items.map((it, idx) => {
          const titleText = it.title || "(No title)";
          const roomText = it.room ? `ROOM ${it.room}` : "—";
          const timeText = it.start || it.end || "—";
          const leftText = formatTimeLeft(it.statusText);
          const rowKey = `${titleText}-${it.room ?? "NA"}-${idx}`;
          const accent = pickRoomColor(it.room);

          return (
            <div
              key={rowKey}
              className="clv-tr"
              style={{ background: idx % 2 ? rowAltBg : rowBg }}
            >
              <div
                className="clv-td clv-titlecell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {titleText}
              </div>

              <div
                className="clv-td clv-roomcell"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ ...pill(accent), fontSize: 11, fontWeight: 800 }}
                >
                  {roomText}
                </span>
              </div>

              <div
                className="clv-td clv-timecell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {timeText}
              </div>

              <div
                className="clv-td clv-leftcell"
                style={{
                  color: textColor,
                  letterSpacing: "0.2px",
                  fontSize: "12px",
                }}
              >
                {leftText}
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <section className="clv-section" style={{ minWidth: 320 }}>
        {showHeaders && (
          <h3
            className="clv-title"
            style={{
              color: textColor,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>{title}</span>

            <span
              style={{
                padding: "2px 12px",
                borderRadius: 999,
                background: "#ffffff",
                color: headerBg,
                fontSize: 12,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: headerBg,
                }}
              />
              {totalCount}
            </span>

            {/* PAGE INDICATOR */}
            {totalPages >= 1 && page >= 1 && (
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: subText,
                  marginLeft: 8,
                }}
              >
                Page {page} / {totalPages}
              </span>
            )}
          </h3>
        )}

        <div
          className="clv-table"
          style={{
            background: cardBg,
            boxShadow:
              "0 8px 12px rgba(0,0,0,0.30), 0 4px 6px rgba(0,0,0,0.18), inset 0 4px 6px rgba(255,255,255,0.16), inset 0 -4px 6px rgba(0,0,0,0.20)",
          }}
        >
          <div className="clv-thead" style={{ background: headerBg }}>
            {cols.map((c) => (
              <div
                key={c.key}
                className="clv-th"
                style={c.key === "room" ? { textAlign: "center" } : undefined}
              >
                {c.label}
              </div>
            ))}
          </div>

          <div className="clv-tbody">
            {items.length === 0 ? (
              <EmptyState accent={DEFAULT_PILL} />
            ) : shouldAnimate ? (
              RowsAnimated
            ) : (
              RowsPlain
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div
      className={`clv-wrap ${className || ""}`}
      style={{ paddingLeft: "50px", paddingRight: "50px" }}
    >
      {isList ? (
        <div className="clv-grid">
          <TableSection
            title="Class Starting Soon"
            items={startItems}
            totalCount={startingSoon.length} // ALL classes
            page={startTotalPages > 0 ? startPage + 1 : 0}
            totalPages={startTotalPages}
            cols={[
              { key: "title", label: "CLASS" },
              { key: "room", label: "ROOM" },
              { key: "start", label: "TIME" },
              { key: "left", label: "TIME REMAIN" },
            ]}
          />

          <TableSection
            title="Class IN Progress"
            items={endItems}
            totalCount={endingSoon.length}
            page={endTotalPages > 0 ? endPage + 1 : 0}
            totalPages={endTotalPages}
            cols={[
              { key: "title", label: "CLASS" },
              { key: "room", label: "ROOM" },
              { key: "end", label: "TIME" },
              { key: "left", label: "TIME REMAIN" },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}
